import { FILE_SCAN_LABELS, type FileScanStatus, type QuarantinedFile } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { KeyValue, Panel } from '../../ui/layout';

const STATUS_KEY = ['admin', 'file-scan', 'status'];
const LIST_KEY = ['admin', 'file-scan', 'quarantine'];

function sizeText(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} МБ`;
}

/**
 * Антивирус и карантин файлов (DEV-10): включена ли проверка, отвечает ли служба, сколько файлов проверено,
 * и файлы, которые не выдаются: найдена угроза или проверить не удалось.
 */
export function FileScanPage() {
  const qc = useQueryClient();
  const status = useQuery({ queryKey: STATUS_KEY, queryFn: () => api<FileScanStatus>('/files/scan-status') });
  const list = useQuery({ queryKey: LIST_KEY, queryFn: () => api<QuarantinedFile[]>('/files/quarantine') });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: STATUS_KEY });
    void qc.invalidateQueries({ queryKey: LIST_KEY });
  };
  const rescan = useMutation({
    mutationFn: (id: string) => api(`/files/${id}/rescan`, { method: 'POST' }),
    onSuccess: () => {
      toast('Файл поставлен на повторную проверку');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/files/${id}/quarantine-delete`, { method: 'POST' }),
    onSuccess: () => {
      toast('Файл удалён из хранилища');
      refresh();
    },
  });

  const s = status.data;
  const counts = s?.counts ?? {};
  const n = (k: string) => counts[k] ?? 0;
  return (
    <div className="flex flex-col gap-6">
      <Panel title="Проверка антивирусом">
        {status.isPending ? (
          <Spinner />
        ) : status.error ? (
          <ErrorBox error={status.error} />
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-[14px] text-ink-2">
              {s!.enabled
                ? 'Новые файлы ждут проверки; найденные угрозы блокируют выдачу. Файлы, загруженные раньше, доступны во время фоновой проверки. Файлы больше предела службы остаются доступными без проверки.'
                : 'Проверка не включена: новые файлы выдаются без проверки антивирусом. Ранее найденные угрозы остаются заблокированы. Включает владелец портала, подключив службу антивируса на сервере.'}
            </p>
            <KeyValue
              items={[
                {
                  label: 'Служба антивируса',
                  value: !s!.enabled ? (
                    <Badge>не подключена</Badge>
                  ) : s!.serviceUp ? (
                    <Badge tone="success">работает</Badge>
                  ) : (
                    <Badge tone="danger">не отвечает</Badge>
                  ),
                },
                { label: FILE_SCAN_LABELS.clean, value: n('clean') },
                { label: FILE_SCAN_LABELS.pending, value: n('pending') },
                { label: 'Не проверены (включая файлы больше предела)', value: n('not_scanned') },
                { label: 'В карантине', value: n('infected') + n('error') },
              ]}
            />
          </div>
        )}
      </Panel>

      <Panel title="Карантин" meta="Эти файлы никому не выдаются">
        {list.isPending ? (
          <Spinner />
        ) : list.error ? (
          <ErrorBox error={list.error} />
        ) : !list.data!.length ? (
          <EmptyState title="Карантин пуст" />
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {list.data!.map((f) => (
              <li key={f.id} className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="min-w-0 font-medium [overflow-wrap:anywhere] text-ink">{f.name}</span>
                  <Badge tone={f.scanState === 'infected' ? 'danger' : 'warning'}>
                    {FILE_SCAN_LABELS[f.scanState]}
                  </Badge>
                </div>
                <div className="text-[13px] text-ink-2">
                  {sizeText(f.sizeBytes)} · загрузил {f.uploadedBy ?? 'служба портала'}{' '}
                  {dateTime(f.createdAt)}
                  {f.attachedTo ? ` · приложен к ${f.attachedTo} карточк.` : ''}
                </div>
                {f.scanResult ? (
                  <div className="text-[13px] [overflow-wrap:anywhere] text-ink-2">
                    {f.scanState === 'infected' ? 'Угроза: ' : 'Причина: '}
                    {f.scanResult}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    disabled={!s?.enabled}
                    loading={rescan.isPending && rescan.variables === f.id}
                    onClick={() => rescan.mutate(f.id)}
                  >
                    Проверить заново
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    loading={remove.isPending && remove.variables === f.id}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Удалить «${f.name}» из хранилища? Восстановить файл будет нельзя, запись останется в истории.`,
                        )
                      )
                        remove.mutate(f.id);
                    }}
                  >
                    Удалить файл
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {rescan.error ? <ErrorBox className="mt-3" error={rescan.error} /> : null}
        {remove.error ? <ErrorBox className="mt-3" error={remove.error} /> : null}
      </Panel>
    </div>
  );
}
